import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent, FilterGroup } from '@custom-components/general/table-filter-bar';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import {
  APP_PATHS,
  COMPANIES_TABLE_COLUMNS_CONFIG,
  COMPANY_TYPES,
  COUNTRIES,
  PAGINATION_DEFAULT,
} from '@core/constants';
import { CompanyPayloadModel } from '@core/models';
import { CompanyStore } from '@root/app/store/company.store';

@Component({
  selector: 'xemex-companies-page',
  imports: [
    TableComponent,
    TableFilterBarComponent,
    PageHeaderComponent,
    PageBodyComponent,
    TranslateModule,
  ],
  providers: [CompanyStore],
  templateUrl: './companies-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './companies-page.component.scss',
})
export class PartnersPageComponent {
  readonly companyStore = inject(CompanyStore);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly columns = COMPANIES_TABLE_COLUMNS_CONFIG;

  protected readonly tableData = computed(
    () => this.companyStore.customerData().partners as unknown as Record<string, unknown>[],
  );

  protected readonly totalCount = computed(() => this.companyStore.customerData().numberOfItems);

  private readonly filterLabels = toSignal(
    this.translate.stream(['companies.filter.companyType', 'companies.filter.country']),
  );

  protected readonly filterGroups = computed<FilterGroup[]>(() => {
    const t = this.filterLabels() ?? {};
    return [
      {
        key: 'companyType',
        label: t['companies.filter.companyType'],
        options: COMPANY_TYPES.map(t => t.index),
      },
      { key: 'country', label: t['companies.filter.country'], options: COUNTRIES.map(c => c.id) },
    ];
  });

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.Partners,
    row['partnerOid'] as string,
  ];

  constructor() {
    this.getPartners();
  }

  private getPartners(): void {
    const partnerOid = this.route.snapshot.queryParamMap.get('partnerOid');
    if (!partnerOid) {
      this.companyStore.getCompaniesMethod(PAGINATION_DEFAULT.offset);
      return;
    }
    this.companyStore.getCompaniesFilterMethod({ partnerOid });
  }

  protected onSearchChange(query: string): void {
    this.companyStore.getCompaniesFilterMethod({ partnerName: query });
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    const payload: CompanyPayloadModel = {
      CompanyType: filters['companyType']?.join(',') ?? '',
      country: filters['country']?.join(',') ?? '',
    };
    this.companyStore.getCompaniesFilterMethod(payload);
  }

  protected onPageChange(evt: PageEvent): void {
    this.companyStore.getCompaniesMethod(evt.pageIndex);
  }

  protected onRefreshClicked(): void {
    this.companyStore.refreshCompaniesMethod();
  }
}
